import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { SignOut, UserCircle } from "@phosphor-icons/react";
import { AVATAR_COLORS } from "@yay/shared";
import { authClient } from "../lib/auth";
import { GoogleG } from "./GoogleG";
import { useCensorPref, setCensorPref } from "../lib/censorPref";
import { loadIdentity, saveIdentity } from "../config";

type SessionUser = {
  id: string;
  email?: string | null;
  name?: string | null;
  image?: string | null;
};

/**
 * TopBar account affordance: a small chip that reveals a blur-fade tooltip on
 * hover and opens an anchored popover on click.
 *
 * Signed out → "Sign in with Google" (redirect flow: Google consent screen is
 * the "bigger popover"; we return with a session cookie).
 * Signed in → email + Sign out.
 *
 * The popover is rendered through a portal with fixed positioning (the same
 * pattern as the theater avatar editor) so no ancestor overflow can clip it.
 */
export function AccountChip() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [photoFailed, setPhotoFailed] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const chipRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Load the session once on mount (cookie round-trip through the proxy),
  // then reconcile the account identity: if the account already has merged
  // prefs, they apply on any device WITHOUT a guest identity (that's the
  // "your name and avatar follow your account" promise); otherwise the
  // guest's visual identity is adopted into the account (one-shot merge).
  useEffect(() => {
    let cancelled = false;
    authClient.getSession().then(async ({ data }) => {
      if (cancelled) return;
      const u = data?.user ?? null;
      setUser(u);
      if (!u) return;

      // Account identity (merged prefs) applies on devices with no guest
      // identity yet. Devices that already have a guest identity keep it —
      // an in-room rename must not be reverted by the account snapshot.
      try {
        const meRes = await fetch("/api/me");
        if (meRes.ok) {
          const meBody = (await meRes.json()) as {
            prefs?: {
              username?: string;
              avatar_seed?: string;
              avatar_color?: string;
            } | null;
          };
          const prefs = meBody?.prefs;
          if (
            prefs &&
            typeof prefs.username === "string" &&
            prefs.username.trim() &&
            typeof prefs.avatar_seed === "string" &&
            prefs.avatar_seed &&
            typeof prefs.avatar_color === "string" &&
            // Only palette colors are valid (the server now rejects junk at
            // merge time; this guards legacy rows written before that check).
            (AVATAR_COLORS as readonly string[]).includes(prefs.avatar_color)
          ) {
            // Merge already ran for this account — stop re-POSTing it.
            localStorage.setItem("yay:merged", "1");
            if (!loadIdentity()) {
              saveIdentity({
                id: crypto.randomUUID(),
                username: prefs.username.slice(0, 20),
                color: prefs.avatar_color,
                avatarSeed: prefs.avatar_seed,
              });
            }
            return;
          }
        }
      } catch {
        /* non-fatal — guest identity stands */
      }

      // First sign-in on this device: adopt the guest identity into the
      // account (one-shot; the server guards on the prefs row).
      if (localStorage.getItem("yay:merged") === "1") return;
      const identity = loadIdentity();
      if (!identity) return;
      try {
        const res = await fetch("/api/identity-merge", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username: identity.username,
            avatarSeed: identity.avatarSeed,
            avatarColor: identity.color,
          }),
        });
        if (res.ok) {
          const body = (await res.json()) as { merged?: boolean };
          if (body.merged) {
            localStorage.setItem("yay:merged", "1");
            setToast(
              "signed in — your name and avatar are now tied to your account",
            );
            setTimeout(() => setToast(null), 4000);
          }
        }
      } catch {
        // Non-fatal: merge retries next load.
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      // Ignore clicks on the chip AND inside the popover — the popover is a
      // portal, so it's not a DOM child of the chip; closing on mousedown
      // would unmount it before the button's click event could fire.
      if (chipRef.current?.contains(t) || popoverRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function toggle() {
    if (!open && chipRef.current) {
      const r = chipRef.current.getBoundingClientRect();
      // Popover's right edge aligns with the chip's right edge, below it.
      setAnchor({ top: r.bottom + 10, left: r.right });
      setError(null);
    }
    setOpen((o) => !o);
  }

  async function handleSignIn() {
    setBusy(true);
    setError(null);
    try {
      // Remember we're mid-sign-in so the app can auto-join on return
      // (the OAuth redirect reloads the page, which would otherwise show
      // the join overlay again).
      sessionStorage.setItem("yay:signin-return", "1");
      const res = await authClient.signIn.social({
        provider: "google",
        callbackURL: `${window.location.origin}${window.location.pathname}`,
      });
      if (res.error) {
        // No provider mounted (e.g. dev without .dev.vars) or network error —
        // re-enable the button and say why instead of leaving it dead.
        setError(res.error.message ?? res.error.statusText ?? "Sign-in failed");
        setBusy(false);
        return;
      }
      // v1.6 client's redirect plugin navigates on { url, redirect: true };
      // fall back to manual navigation if it didn't.
      if (res.data?.url) window.location.assign(res.data.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed");
      setBusy(false);
    }
  }

  async function handleSignOut() {
    setBusy(true);
    setError(null);
    try {
      await authClient.signOut();
      setUser(null);
      setPhotoFailed(false);
      setOpen(false);
    } catch {
      setError("Sign-out failed");
    } finally {
      setBusy(false);
    }
  }

  const label = user ? (user.name ?? user.email ?? "Account") : "Sign in";
  const censorOn = useCensorPref();

  return (
    <>
      <button
        ref={chipRef}
        type="button"
        className="account-chip"
        onClick={toggle}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        {user?.image && !photoFailed ? (
          <img
            className="account-chip-photo"
            src={user.image}
            alt=""
            referrerPolicy="no-referrer"
            onError={() => setPhotoFailed(true)}
          />
        ) : (
          <UserCircle size={16} weight="bold" />
        )}
        <span className="account-chip-tip" role="tooltip">
          {label}
        </span>
      </button>

      {open &&
        anchor &&
        createPortal(
          <div
            className="account-popover"
            role="dialog"
            aria-label={label}
            ref={popoverRef}
            style={{
              top: anchor.top,
              left: anchor.left,
              transform: "translateX(-100%)",
            }}
          >
            {user ? (
              <>
                <p className="account-email" title={user.email ?? undefined}>
                  {user.email ?? "Signed in"}
                </p>
                <button
                  type="button"
                  className="account-signout"
                  onClick={handleSignOut}
                  disabled={busy}
                >
                  {busy ? <span className="account-spinner" /> : <SignOut size={16} weight="bold" />}
                  Sign out
                </button>
              </>
            ) : (
              <>
                <p className="account-popover-lead">
                  Sign in to keep your name and avatar on any device — premium
                  attaches to your account when it ships.
                </p>
                <button
                  type="button"
                  className="account-google-btn"
                  onClick={handleSignIn}
                  disabled={busy}
                >
                  {busy ? <span className="account-spinner" /> : <GoogleG size={18} />}
                  Sign in with Google
                </button>
                {error && <p className="account-popover-error">{error}</p>}
              </>
            )}

            <div className="account-settings">
              <span className="account-settings-label">
                hide offensive words
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={censorOn}
                aria-label="Hide offensive words"
                className={`account-switch${censorOn ? " on" : ""}`}
                onClick={() => setCensorPref(!censorOn)}
              >
                <span className="account-switch-knob" />
              </button>
            </div>
          </div>,
          document.body,
        )}
      {toast && (
        <div className="account-toast" role="status">
          {toast}
        </div>
      )}
    </>
  );
}
